import React, { useState, useEffect } from "react";
import { BiEdit, BiMinusCircle, BiPlusCircle, BiShow, BiTrash } from "react-icons/bi";
import { PermissionEnum } from "../enum/permission.enum";
import { RoleEnum } from "../enum/role.enum";
import { ReadModuloDto } from "@/api.types";

type Props = {
  dtoModules?: ReadModuloDto[] | null;
  handleSave: (lista: ReadModuloDto[]) => void;
};

type GruposDefinidos = Record<string, RoleEnum[]>;

const gruposConfigurados: GruposDefinidos = {
  Ações: [
    RoleEnum.ATUACAO,
    RoleEnum.FOLHA,
    RoleEnum.FOLHA_SEND_NOTIFICATION,
    RoleEnum.GERENCIAR_REGRAS,
    RoleEnum.LOTACAO,
    RoleEnum.MOVIMENTACAO,
    RoleEnum.NUCLEO_MAX_ESTAGIARIOS,
    RoleEnum.PERIODO,
    RoleEnum.SUBSTITUICAO,
  ],
  Pessoas: [RoleEnum.PESSOA, RoleEnum.DEFENSOR, RoleEnum.ESTAGIARIO, RoleEnum.COLABORADOR],
  Diversos: [
    RoleEnum.COMARCA,
    RoleEnum.CURSO,
    RoleEnum.DEFENSORIA,
    RoleEnum.DISTRITO,
    RoleEnum.FERIADO,
    RoleEnum.INSTITUICAO_ENSINO,
    RoleEnum.NUCLEO,
    RoleEnum.PREDIO,
    RoleEnum.VARA,
  ],
  Administração: [
    RoleEnum.ADMIN_ACTIONS,
    RoleEnum.PAPEL,
    RoleEnum.SOLAR,
    RoleEnum.USUARIO,
  ],
  Outros: []
};

function agruparPorGrupo(modulos: ReadModuloDto[]): Record<string, ReadModuloDto[]> {
  const grupos: Record<string, ReadModuloDto[]> = {};
  for (const [nome, roles] of Object.entries(gruposConfigurados)) {
    grupos[nome] = modulos.filter(m => roles.includes(m.modulo.role));
  }

  const classificados = Object.values(gruposConfigurados).flat();
  const naoClassificados = modulos.filter(m => !classificados.includes(m.modulo.role));
  if (naoClassificados.length)
    grupos["Outros"] = naoClassificados;

  return grupos;
}

export const TabelaDePermissoes: React.FC<Props> = ({ dtoModules, handleSave }) => {
  const permissions = Object.values(PermissionEnum).filter(p => typeof p === "string");
  const allRoles = Object.entries(RoleEnum).filter(([key]) => isNaN(Number(key)));

  const [acessos, setAcessos] = useState<Record<RoleEnum, PermissionEnum>>({} as Record<RoleEnum, PermissionEnum>);
  const [modulos, setModulos] = useState<ReadModuloDto[]>([]);

  useEffect(() => {
    const lista = allRoles.map(([, roleValue]) => {
      const existente = dtoModules?.find(m => m.modulo.role === roleValue);
      return { modulo: { role: roleValue as RoleEnum }, nivel: existente?.nivel ?? PermissionEnum.NONE };
    }) as ReadModuloDto[];

    if (!dtoModules || dtoModules.length === 0) handleSave(lista);

    const inicial: Record<RoleEnum, PermissionEnum> = {} as Record<RoleEnum, PermissionEnum>;
    lista.forEach(m => (inicial[m.modulo.role as RoleEnum] = m.nivel ?? PermissionEnum.NONE));

    setAcessos(inicial);
    setModulos(lista);
  }, [dtoModules]);

  const handleChange = (role: RoleEnum, nivel: PermissionEnum) => {
    setAcessos(prev => {
      const novo = { ...prev, [role]: nivel };
      const modulosAtualizados = modulos.map(m => (m.modulo.role === role ? { ...m, nivel } : m));
      setModulos(modulosAtualizados);
      handleSave(modulosAtualizados);
      return novo;
    });
  };

  const renderIcon = (nivel: PermissionEnum, marcado: boolean) => {
    const size = 20;
    const desmarcado = "text-secondary";
    switch (nivel) {
      case PermissionEnum.NONE:
        return <BiMinusCircle size={size} className={marcado ? "text-black" : desmarcado} title="Sem acesso" />;
      case PermissionEnum.READ:
        return <BiShow size={size} className={marcado ? "text-primary" : desmarcado} title="Ler" />;
      case PermissionEnum.CREATE:
        return <BiPlusCircle size={size} className={marcado ? "text-success" : desmarcado} title="Criar" />;
      case PermissionEnum.UPDATE:
        return <BiEdit size={size} className={marcado ? "text-warning" : desmarcado} title="Atualizar" />;
      case PermissionEnum.DELETE:
        return <BiTrash size={size} className={marcado ? "text-danger" : desmarcado} title="Remover" />;
      default:
        return null;
    }
  };

  const grupos = agruparPorGrupo(modulos);

  return (
    <div className="row">
      {Object.entries(grupos).filter(a => a[1].length > 0).map(([nomeGrupo, lista]) => (
        <div key={nomeGrupo} className="col-md-4 mb-3">
          <h6 className="fw-bold mb-2">{nomeGrupo}</h6>
          <table className="table table-bordered table-hover p-0 align-middle text-center">
            <thead className="table-light">
              <tr>
                <th className="text-start">Módulo</th>
                <th>Acesso</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(mod => {
                const role = mod.modulo.role;
                return (
                  <tr key={role}>
                    <td className="text-start">{RoleEnum[role]}</td>
                    <td className="p-0 pb-1 pt-1 m-0">
                      {permissions.map(permissao => {
                        const permValue = PermissionEnum[permissao as keyof typeof PermissionEnum];
                        const marcado = acessos[role] === permValue;
                        return (
                          <label key={permissao} title={permissao} className="mx-1 p-0">
                            <input
                              type="radio"
                              name={`acesso-${role}`}
                              value={permValue}
                              checked={marcado}
                              onChange={() => handleChange(role, permValue)}
                              className="d-none"
                            />
                            <span
                              className={`rounded d-inline-flex ${marcado ? "bg-light border border-secondary" : ""}`}
                              style={{ cursor: "pointer" }}
                            >
                              {renderIcon(permValue, marcado)}
                            </span>
                          </label>
                        );
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};